import { useUpdateBrief } from "@entities/brief/lib/hooks/useUpdateBrief";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Input } from "@shared/ui";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useBriefDownload } from "./hooks/useBriefDownload";
import { useCampaignBriefAutosave } from "./hooks/useCampaignBriefAutosave";
import styles from "./styles.module.scss";
import { BriefEditorSection } from "./ui/BriefEditorSection";

type UnifiedCampaignSetupProps = {
  showCampaignName?: boolean;
  showOutreachTerms?: boolean;
};

export function UnifiedCampaignSetup({
  showCampaignName = true,
  showOutreachTerms = true,
}: UnifiedCampaignSetupProps) {
  const { brief, briefId, isLoadingBrief } = useBriefStore();
  const [savedFlash, setSavedFlash] = useState(false);
  const [name, setName] = useState("");
  const [pricingOutreachTerms, setPricingOutreachTerms] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  // const [isImporting, setIsImporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<"ok" | "err">("ok");
  // const [isUploadConfirmOpen, setIsUploadConfirmOpen] = useState(false);

  const editorRef = useRef<EditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef(name);
  nameRef.current = name;

  const { updateBrief, isUpdating: isUpdatingBrief } = useUpdateBrief();

  const {
    isUpdating: isUpdatingForm,
    scheduleSave,
    saveImmediately,
    pauseAutosaveForMedia,
    saveAfterMediaUpload,
    resetSnapshot,
    clearPending,
  } = useCampaignBriefAutosave({
    briefId,
    editorRef,
    nameRef,
    onSaved: () => setSavedFlash(true),
  });

  const isUpdating = isUpdatingForm || isUpdatingBrief;

  const showStatus = (message: string, tone: "ok" | "err" = "ok") => {
    setStatusTone(tone);
    setStatusMessage(message);
  };

  const { isDownloading, downloadLabel, handleDownloadDoc, handleDownloadPdf } =
    useBriefDownload({
      editorRef,
      campaignName: name,

      showStatus,
      onMenuClose: () => setDownloadOpen(false),
    });

  useEffect(() => {
    clearPending();
    const nextName = brief?.name || "";
    const nextTerms = brief?.pricingOutreachTerms || "";
    setName(nextName);
    nameRef.current = nextName;
    setPricingOutreachTerms(nextTerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate on campaign switch only
  }, [briefId]);

  useEffect(() => {
    if (!savedFlash) return;
    const t = setTimeout(() => setSavedFlash(false), 1200);
    return () => clearTimeout(t);
  }, [savedFlash]);

  useEffect(() => {
    if (!statusMessage || isDownloading) return;
    const t = setTimeout(() => setStatusMessage(null), 3200);
    return () => clearTimeout(t);
  }, [statusMessage, isDownloading]);

  useEffect(() => {
    if (!downloadOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        downloadMenuRef.current &&
        !downloadMenuRef.current.contains(e.target as Node)
      ) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [downloadOpen]);

  useEffect(() => {
    if (!downloadOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDownloadOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [downloadOpen]);

  const handleNameChange = (value: string) => {
    setName(value);
    nameRef.current = value;
    scheduleSave();
  };

  const savePricingOutreachTerms = (value: string) => {
    if (!briefId) return;
    updateBrief({
      updatedBrief: {
        briefId,
        pricingOutreachTerms: value,
        brandId: brief?.brandId,
      },
    });
  };

  const debouncedSaveReachout = useDebouncedCallback((val: string) => {
    savePricingOutreachTerms(val);
  }, 800);

  const handlePricingOutreachTermsChange = (value: string) => {
    setPricingOutreachTerms(value);
    debouncedSaveReachout(value);
  };


  const handleEditorReady = () => {
    const html =
      editorRef.current?.getHTML({ syncTokens: false }) ?? brief?.formV2 ?? "";
    resetSnapshot(html, brief?.name || "");
    if ((nameRef.current || "") !== (brief?.name || "")) {
      scheduleSave();
    }
  };

  if (!briefId || (isLoadingBrief && !brief)) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Loading campaign…</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showCampaignName ? (
        <section className={styles.campaignDetailsSection}>
          <div className={styles.titleArea}>
            <div className={styles.labelRow}>
              <label className={styles.campaignLabel} htmlFor="campaign-name">
                Campaign name
              </label>
              {savedFlash && <span className={styles.savedBadge}>Saved</span>}
              {isUpdating && !savedFlash && (
                <span className={styles.savingBadge}>Saving…</span>
              )}
            </div>
            <div className={styles.titleInputWrap}>
              <Input
                id="campaign-name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                className={styles.titleInput}
                variant="borderless"
                placeholder="Untitled campaign"
              />
            </div>
          </div>
        </section>
      ) : null}

      <BriefEditorSection
        briefId={briefId}
        initialHtml={brief?.formV2 || ""}
        // isImporting={isImporting}
        isDownloading={isDownloading}
        downloadLabel={downloadLabel}
        downloadOpen={downloadOpen}
        statusMessage={statusMessage}
        statusTone={statusTone}
        editorRef={editorRef}
        fileInputRef={fileInputRef}
        downloadMenuRef={downloadMenuRef}
        onToggleDownload={() => {
          // if (isDownloading || isImporting) return;
          setDownloadOpen((v) => !v);
        }}
        // onUploadClick={() => setIsUploadConfirmOpen(true)}
        // onFileChange={handleFileChange}
        onDownloadDoc={handleDownloadDoc}
        onDownloadPdf={handleDownloadPdf}
        onDismissStatus={() => setStatusMessage(null)}
        onEditorChange={scheduleSave}
        onMediaUploadStart={pauseAutosaveForMedia}
        onMediaUploadComplete={saveAfterMediaUpload}
        onEditorReady={handleEditorReady}
      />

      {showOutreachTerms ? (
        <section className={styles.reachoutTermsSection}>
          <div className={styles.reachoutTermsHeader}>
            <label
              className={styles.reachoutTermsLabel}
              htmlFor="campaign-reachout-terms"
            >
              Campaign Reachout Terms
            </label>
            <span className={styles.reachoutTermsSubtitle}>
              Synced to My Pricing Creator guidelines and the pinned Outreach
              Terms note.
            </span>
          </div>
          <div className={styles.reachoutTermsInputWrap}>
            <Input.TextArea
              id="campaign-reachout-terms"
              value={pricingOutreachTerms}
              onChange={(e) => handlePricingOutreachTermsChange(e.target.value)}
              className={styles.reachoutTermsTextArea}
              placeholder="Add campaign reachout terms..."
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
          </div>
        </section>
      ) : null}

      {/* <Modal
        title="Document Upload Notice"
        open={isUploadConfirmOpen}
        onOk={() => {
          setIsUploadConfirmOpen(false);
          fileInputRef.current?.click();
        }}
        onCancel={() => setIsUploadConfirmOpen(false)}
        okText="Continue"
        cancelText="Cancel"
      >
        <div className={styles.uploadModalBody}>
          <p className={styles.uploadModalText}>
            Importing will replace the current master brief. Styling may also
            differ from the original file.
          </p>
          <p className={styles.uploadModalText}>
            If the DOC contains video, it will not be uploaded or imported into
            the brief.
          </p>
          <p className={styles.uploadModalEmphasis}>Do you want to continue?</p>
        </div>
      </Modal> */}
    </div>
  );
}

export default UnifiedCampaignSetup;
